import clsx from "clsx";
import { useFormik } from 'formik';
import { useEffect, useState } from "react";
import { Modal } from 'react-bootstrap-v5';
import MaskedInput from "react-text-mask";
import * as Yup from 'yup';
import { useAuth } from "../../contexts/AuthProvider";
import { IUsuarios } from "../../interfaces/IUsuarios";
import { IDepartamento } from "../../interfaces/IDepartamento";
import EmpresasService from "../../services/EmpresasService";
import UsuariosService from "../../services/UsuariosService";
import DepartamentoService from "../../services/DepartamentoService";
import { MASCARA_CPF, MASCARA_TELEFONE } from "../../utils/Constants";
import workflowService from "../workflow-aprovacao/workflowService";

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

const UsuariosModal = ({ toggleModal, show, usuario, title }: { toggleModal: (loadItens?: boolean) => void, show: boolean, usuario: IUsuarios, title: string }) => {
    const [empresaSelecionada, setEmpresaSelecionada] = useState<any>();
    // Lista mestra e imutável de todas as empresas do cliente. Nunca é mutada diretamente;
    // a divisão entre "vinculadas" e "disponíveis" é sempre derivada dela + empresaIdsVinculadas,
    // para não vazar estado de um usuário para outro ao trocar o usuário em edição.
    const [empresas, setEmpresas] = useState<any[]>([]);
    const [empresaIdsVinculadas, setEmpresaIdsVinculadas] = useState<number[]>([]);
    const [empresaAdicionadaSelecionada, setEmpresaAdicionadaSelecionada] = useState<any>();
    const [pesquisaEmpresa, setPesquisaEmpresa] = useState<string>('');

    const empresasAdicionadas = empresas
        .filter((e) => empresaIdsVinculadas.includes(e.id))
        .sort((a, b) => a.nome > b.nome ? 1 : -1);

    const empresasNaoVinculadas = empresas.filter((e) => !empresaIdsVinculadas.includes(e.id));
    const empresasVisualizacao = pesquisaEmpresa
        ? empresasNaoVinculadas.filter((e) => e.nome?.toLowerCase().indexOf(pesquisaEmpresa.toLowerCase()) >= 0)
        : empresasNaoVinculadas;
    const [departamentos, setDepartamentos] = useState<IDepartamento[]>([]);
    const [mostrarNovoDepartamento, setMostrarNovoDepartamento] = useState<boolean>(false);
    const [novoDepartamentoNome, setNovoDepartamentoNome] = useState<string>('');
    const { usuario: usuarioLogado } = useAuth();
    const { t } = useTranslation();

    // Workflow TI — independente do formik, gerenciado separadamente
    const [wfTiAdmin, setWfTiAdmin] = useState(false);

    const obrigacoesSchema = Yup.object().shape({
        nome: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        email: Yup.string().email().max(50, 'Maximo 50 caracteres').email('Esse e-mail não é válido').required('Descrição é obrigatório'),
        cpf: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        telefone: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        registroProfissional: Yup.string().nullable().max(50, 'Maximo 50 caracteres'),
        nomeUsuario: Yup.string().max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório'),
        perfil: Yup.string().min(2).max(50, 'Maximo 50 caracteres').required('Descrição é obrigatório')
    })
    const initialValues = {
        id: 0,
        nome: '',
        email: '',
        cpf: '',
        telefone: '',
        registroProfissional: '',
        nomeUsuario: '',
        perfil: '',
        responsavelContabil: false,
        adminMaestroContabil: false
    } as IUsuarios;
    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: obrigacoesSchema,
        onSubmit: async (values, { setSubmitting }) => {
            values.clienteID = usuarioLogado?.clienteID;
            let savedUserId: number | undefined;

            values.empresas = empresasAdicionadas.map(e => e.id);

            if (values.id === 0) {
                await UsuariosService.criar(values, (result) => {
                    savedUserId = result?.id ?? result;
                }, t('Usuário foi adicionado com sucesso'));
            } else {
                const ok = await UsuariosService.atualizar(values, undefined, t('Usuário foi editado com sucesso'));
                if (ok) savedUserId = values.id;
            }

            if (savedUserId) {
                // Sincronizar permissão de Administrador TI no Workflow — independente do
                // nível de aprovação (N1-N7) — não bloqueia o fluxo principal
                try {
                    await workflowService.setAdminTi({
                        authUserId: savedUserId,
                        nome:       values.nome,
                        email:      values.email,
                        ativo:      wfTiAdmin,
                    });
                } catch { }
                toggleModal(true);
            } else {
                setSubmitting(false);
            }
        }
    });
    const perfilAdmin = [
        { id: '2',  perfil: t('Administrador') },
        { id: '3', perfil: t('Analista') }
    ];

    useEffect(() => {
        formik.resetForm();

        if (usuario && usuario.id > 0) {
            formik.setValues(usuario);
            setEmpresaIdsVinculadas(usuario.empresas?.map((e) => typeof e === 'number' ? e : e.id) ?? []);
        } else {
            setEmpresaIdsVinculadas([]);
        }

        setEmpresaSelecionada(null);
        setEmpresaAdicionadaSelecionada(null);
        setPesquisaEmpresa('');

        !usuario.nome && formik.setFieldTouched('nome', false);
        !usuario.email && formik.setFieldTouched('email', false);
        !usuario.cpf && formik.setFieldTouched('cpf', false);
        !usuario.telefone && formik.setFieldTouched('telefone', false);
        !usuario.nomeUsuario && formik.setFieldTouched('nomeUsuario', false);
        !usuario.perfil && formik.setFieldTouched('perfil', false);

        // eslint-disable-next-line
    }, [usuario]);

    useEffect(() => {

        async function preCarregar() {
            if (usuarioLogado?.clienteID) {
                const todasEmpresas = await EmpresasService.obterTodosPorClienteID(usuarioLogado.clienteID | 0);

                setEmpresas(todasEmpresas);
            }

            const todosDepartamentos = await DepartamentoService.obterTodos();
            setDepartamentos(todosDepartamentos);
        }

        preCarregar();
    }, []);

    // Carrega status de Administrador TI do Workflow ao abrir o modal — permissão
    // independente do nível de aprovação (N1-N7).
    useEffect(() => {
        if (!show) return;
        setWfTiAdmin(false);
        if (usuario?.id > 0) {
            workflowService.listarUsuarios().then(wfUsuarios => {
                const wfUser = wfUsuarios.find(u => u.authUserId === usuario.id && u.adminTi && u.ativo);
                if (wfUser) setWfTiAdmin(true);
            }).catch(() => {});
        }
    }, [show, usuario?.id]);

    async function criarDepartamentoInline() {
        if (!novoDepartamentoNome.trim()) return;

        const criado = await DepartamentoService.criar(novoDepartamentoNome.trim());
        setDepartamentos((atual) => [...atual, criado].sort((a, b) => a.nome.localeCompare(b.nome)));
        await formik.setFieldValue('departamentoID', criado.id);
        setNovoDepartamentoNome('');
        setMostrarNovoDepartamento(false);
    }

    return <>
        <Modal size='lg' centered={true} show={show} onHide={toggleModal}>
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title titulo-modal">{title}</h5>
                    <div className="btn btn-icon btn-sm btn-active-light-primary ms-2">
                        <i onClick={() => toggleModal()} className="fas fa-times" style={{ color: '#5E6397' }}></i>
                    </div>
                </div>
                <div className="modal-body">
                    <form className="row g-3">
                        <div className="col-md-7">
                            <label htmlFor="form-nome" className="form-label required">{t("Nome")}</label>
                            <input {...formik.getFieldProps('nome')} placeholder={t("Nome Completo")} type="text" className={clsx(
                                'form-control',
                                {
                                    'is-invalid': formik.touched.nome && formik.errors.nome,
                                },
                                {
                                    'is-valid': formik.touched.nome && !formik.errors.nome,
                                }
                            )} id="form-nome" />
                        </div>
                        <div className="col-md-5">
                            <label htmlFor="form-email" className="form-label required">E-mail</label>
                            <input {...formik.getFieldProps('email')} placeholder={t("exemplo@email.com")} type="text" className={clsx(
                                'form-control',
                                {
                                    'is-invalid': formik.touched.email && formik.errors.email,
                                },
                                {
                                    'is-valid': formik.touched.email && !formik.errors.email,
                                }
                            )} id="form-email" />
                        </div>
                        <div className="col-md-3">
                            <label htmlFor="form-usuario" className="form-label required mt-5">{t("Nome de Usuário")}</label>
                            <input {...formik.getFieldProps('nomeUsuario')} placeholder={t("Digite algo")} type="text" className={clsx(
                                'form-control',
                                {
                                    'is-invalid': formik.touched.nomeUsuario && formik.errors.nomeUsuario,
                                },
                                {
                                    'is-valid': formik.touched.nomeUsuario && !formik.errors.nomeUsuario,
                                }
                            )} id="form-usuario" />
                        </div>
                        <div className="col-md-3">
                            <label htmlFor="form-cpf" className="form-label required mt-5">{t('CPF')}</label>
                            <MaskedInput mask={MASCARA_CPF} {...formik.getFieldProps('cpf')} placeholder="000.000.000-00" type="text" className={clsx(
                                'form-control',
                                {
                                    'is-invalid': formik.touched.cpf && formik.errors.cpf,
                                },
                                {
                                    'is-valid': formik.touched.cpf && !formik.errors.cpf,
                                }
                            )} id="form-cpf" />
                        </div>
                        <div className="col-md-3">
                            <label htmlFor="form-telefone" className="form-label required mt-5">{t("Telefone")}</label>
                            <MaskedInput mask={MASCARA_TELEFONE} {...formik.getFieldProps('telefone')} placeholder="(00)0000-0000" type="text" className={clsx(
                                'form-control',
                                {
                                    'is-invalid': formik.touched.telefone && formik.errors.telefone,
                                },
                                {
                                    'is-valid': formik.touched.telefone && !formik.errors.telefone,
                                }
                            )} id="form-telefone" />
                        </div>
                        <div className="col-md-3">
                            <label htmlFor="form-perfil" className="form-label required mt-5">{t("Perfil")}</label>
                            <select id="form-perfil"
                                {...formik.getFieldProps('perfil')}
                                onChange={async e => await formik.setFieldValue(e.target.name, e.target.value)} className="form-select">
                                <option value="" hidden>{t("Selecione")}</option>
                                {
                                    perfilAdmin.length > 0 && perfilAdmin.map((x, index) => <option key={index} value={x.perfil}>{x.perfil}</option>)
                                }
                            </select>
                            <label style={{ color: 'red' }} hidden={!(formik.touched.perfil && formik.errors.perfil)}>{formik.errors.perfil}</label>
                        </div>
                        <div className="col-md-6">
                            <label htmlFor="form-registro-profissional" className="form-label mt-5">{t("Registro Profissional")}</label>
                            <input {...formik.getFieldProps('registroProfissional')} value={formik.values.registroProfissional ?? ''} maxLength={50} placeholder="CRC-SP 000000/O-0" type="text" className={clsx(
                                'form-control',
                                {
                                    'is-invalid': formik.touched.registroProfissional && formik.errors.registroProfissional,
                                }
                            )} id="form-registro-profissional" />
                        </div>
                        <div className="col-md-4" style={{ width: '100%', marginTop: '20px' }}>
                            <label className='form-check-label fw-bold'>
                                {t("Responsável Contabil")}
                            </label>
                            <input
                                {...formik.getFieldProps('responsavelContabil')}
                                checked={formik.values['responsavelContabil']}
                                style={{ marginLeft: '15px' }}
                                className='form-check-input'
                                type='checkbox'
                            />
                        </div>

                        {/* Módulo Workflow — Administrador TI */}
                        <div style={{ width: '100%', marginTop: '16px', padding: '14px 16px', background: '#f8f9fb', border: '1px solid #e4e6ef', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                            <input
                                id="wf-ti-admin"
                                type="checkbox"
                                className='form-check-input'
                                style={{ marginTop: '3px', flexShrink: 0 }}
                                checked={wfTiAdmin}
                                onChange={e => setWfTiAdmin(e.target.checked)}
                            />
                            <div>
                                <label htmlFor="wf-ti-admin" className='form-check-label fw-bold' style={{ cursor: 'pointer', fontSize: '13px' }}>
                                    Administrador TI do Workflow SOX
                                </label>
                                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#7e8299', lineHeight: 1.4 }}>
                                    Habilita acesso de configuração ao módulo de Workflow SOX (grupos, alçadas, SLA, cargos, usuários) — independente de nível de aprovação. Para aprovar conciliações (N1-N7), atribua um nível separadamente no módulo Workflow.
                                </p>
                            </div>
                        </div>

                        {/* Módulo Maestro Contábil — Administrador */}
                        <div style={{ width: '100%', marginTop: '16px', padding: '14px 16px', background: '#f8f9fb', border: '1px solid #e4e6ef', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                            <input
                                id="maestro-admin"
                                type="checkbox"
                                className='form-check-input'
                                style={{ marginTop: '3px', flexShrink: 0 }}
                                {...formik.getFieldProps('adminMaestroContabil')}
                                checked={formik.values['adminMaestroContabil']}
                            />
                            <div>
                                <label htmlFor="maestro-admin" className='form-check-label fw-bold' style={{ cursor: 'pointer', fontSize: '13px' }}>
                                    Administrador do Maestro Contábil
                                </label>
                                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#7e8299', lineHeight: 1.4 }}>
                                    Habilita acesso de configuração ao módulo Maestro Contábil (Setup, Usuários do Maestro, reset) — independente do perfil geral da plataforma. Quem conduz o Setup deve estar marcado aqui.
                                </p>
                            </div>
                        </div>

                        <div className="col-md-5 mt-5">
                            <label htmlFor="form-departamento" className="form-label">{t("Departamento / Área Responsável")}</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <select id="form-departamento"
                                    {...formik.getFieldProps('departamentoID')}
                                    onChange={async e => await formik.setFieldValue(e.target.name, e.target.value ? parseInt(e.target.value) : undefined)}
                                    className="form-select">
                                    <option value="">{t("Selecione")}</option>
                                    {departamentos.map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
                                </select>
                                <button type="button" className="btn btn-sm btn-primario" onClick={() => setMostrarNovoDepartamento((v) => !v)}>
                                    +
                                </button>
                            </div>
                            {mostrarNovoDepartamento && (
                                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t("Nome do novo departamento") as string}
                                        value={novoDepartamentoNome}
                                        onChange={(e) => setNovoDepartamentoNome(e.target.value)}
                                    />
                                    <button type="button" className="btn btn-sm btn-primario" onClick={criarDepartamentoInline}>
                                        {t("Adicionar")}
                                    </button>
                                </div>
                            )}
                        </div>
                    </form>
                    <div style={{ display: 'flex', marginTop: '30px' }}>
                        <div className="col-md-5 ms-5" style={{ border: '1px solid #DFDFDF', textAlign: 'left', borderRadius: '10px', padding: '10px', margin: '0' }}>
                            <h1 className="azul-agriholmes mt-5 mb-5" style={{ textAlign: 'center' }}>{t("Empresas Vinculadas")}</h1>
                            {
                                empresasAdicionadas.map((x, index) => {
                                    function acaoTocarEmpresasAdicionadas() {
                                        if (empresaAdicionadaSelecionada?.id === x.id) {
                                            setEmpresaAdicionadaSelecionada(null);

                                            return;
                                        }

                                        setEmpresaAdicionadaSelecionada(x);
                                        setEmpresaSelecionada(null);
                                    }

                                    return <p key={index} className="parametrizacao-item" onClick={acaoTocarEmpresasAdicionadas}>{x.nomeFantasia || x.razaoSocial}</p>
                                })
                            }
                        </div>
                        <div className="col-md-1 align-items-center ms-1 mx-1" style={{ padding: '10px', margin: '0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <div className='align-items-center py-1'>
                                <button disabled={empresaSelecionada == null} onClick={() => {
                                    setEmpresaIdsVinculadas((atual) => [...atual, empresaSelecionada.id]);
                                    setEmpresaSelecionada(null);
                                }} className='btn btn-sm btn-primario'>
                                    <i className="text-white fa fa-arrow-left"></i>
                                </button>
                            </div>
                            <div className='align-items-center py-1'>
                                <button disabled={empresaAdicionadaSelecionada == null} onClick={() => {
                                    setEmpresaIdsVinculadas((atual) => atual.filter((id) => id !== empresaAdicionadaSelecionada.id));
                                    setEmpresaAdicionadaSelecionada(null);
                                }} className='btn btn-sm btn-primario'>
                                    <i className="text-white fa fa-arrow-right"></i>
                                </button>
                            </div>
                        </div>
                        <div className="col-md-5" style={{ border: '1px solid #DFDFDF', textAlign: 'left', borderRadius: '10px', padding: '10px' }}>
                            <h1 className="azul-agriholmes mt-5 mb-5" style={{ textAlign: 'center' }}>{t("Todas as Empresas")}</h1>
                            <div style={{ border: '1px solid #DDD', borderRadius: '10px', padding: '10px' }}>
                                <i className="bi bi-search" />
                                <input onChange={(e) => { setPesquisaEmpresa(e.target.value); setEmpresaSelecionada(null); }} placeholder={t("Digite algo")} style={{ border: 'none', outline: 'none', width: '90%', marginLeft: '5px' }} />
                            </div>
                            <div style={{ overflow: 'auto', minHeight: '200px', maxHeight: '200px' }} onLoad={() => setPesquisaEmpresa('')}>
                                {
                                    empresasVisualizacao.length > 0
                                        ?
                                        empresasVisualizacao.map((x, index) => {
                                            function acaoTocarEmpresasVisualizacao() {
                                                if (empresaSelecionada?.id === x.id) {
                                                    setEmpresaSelecionada(null);

                                                    return;
                                                }

                                                setEmpresaSelecionada(x);
                                                setEmpresaAdicionadaSelecionada(null);
                                            }

                                            return <p key={index} onClick={acaoTocarEmpresasVisualizacao} className="parametrizacao-item">{x.nomeFantasia || x.razaoSocial}</p>
                                        })
                                        :
                                        <p className="mt-5 ms-5">{t("Não foi encontrada nenhuma empresa")}</p>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-footer" style={{ margin: '0 auto' }}>
                    <button disabled={formik.isSubmitting} onClick={() => toggleModal()} type="button" className="btn btn-modal-cancelar me-5"> {t("CANCELAR")} </button>
                    <button disabled={formik.isSubmitting || !formik.isValid} onClick={() => { formik.submitForm() }} type="button" className="btn btn-primario ms-5">
                        {!formik.isSubmitting && <> {usuario?.id ? t('EDITAR') : t('ADICIONAR')} </>}
                        {formik.isSubmitting && (
                            <span className='indicator-progress' style={{ display: 'block' }}>
                                {t("Salvando...")}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    </>
}

export default UsuariosModal;